import { memo, useCallback, useState, useMemo } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Box, Typography, Button, useTheme, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { GetEditorialAssetIdLockResult, GetUserMeData } from 'utility/types/dataProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { bloxValues } from 'themes/constants';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { t } from 'i18next';
import { formatAuthorData } from 'utility/functions';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  NXT_LOCK_ORIGIN,
  LEGACY_LOCK_ORIGIN,
} from 'providers/contexts/extensions/Editorial';
import {
  initReadOnlyState,
  isAssetLocked,
  isAssetLockedByNXT,
  isAssetLockedByUser,
} from '../../context/AssetConfigUtilities';
import { useCosmosRuntime } from 'providers/contexts/CosmosRuntimeContext';
import { useAnalytics } from 'providers/contexts/analytics/AnalyticsContext';
import { getBackToSearchLink } from 'components/Breadcrumbs/utility/breadcrumbUtils';
import { ConfirmationDialog } from 'components/generic/ConfirmationDialog';
import { useAssetConfigContext } from '../../context/AssetConfigContextProvider';
import {
  checkSiteAdminPermission,
  checkModifyAssetsPermission,
} from 'utility/permissions';

/**
 * Convert the origin string to a more user-friendly name.
 * Falls back to the origin string if no match is found.
 *
 * Note: Discussed with Jon about Total CMS / InDesign
 * lock origin strings, but they dont currently have a prefix
 * substring or solid pattern, as in that env origin locks
 * are acquired per page eg "2025_03_13_CTH_A01", the
 * naming convention is unique to each site, with a year
 * month, date, sometimes year is two digit, and sometimes
 * separated by underscores, or dashes, or none at all. So in
 * this case seem to be okay with just falling through
 * and displaying the raw origin string as is.
 */
export function getLockOriginName(origin?: string) {
  switch (origin) {
    case NXT_LOCK_ORIGIN:
      return 'NXT Admin';
    case LEGACY_LOCK_ORIGIN:
      return 'Legacy Admin';
    default:
      return origin;
  }
}

export interface AssetLockedProps {
  lockData?: GetEditorialAssetIdLockResult['data'];
  userUuid?: GetUserMeData['uuid']; // current active user uuid, not lock owner uuid
  isCollabMode?: boolean;
}

function buildLockoutInfo({ lockData, userUuid, isCollabMode }: AssetLockedProps) {
  const isLockedAnywhere = isAssetLocked(lockData);
  const isLockedByNXT = isAssetLockedByNXT(lockData);
  const isLockedByThisUser = isAssetLockedByUser(lockData, userUuid);

  const lockProps = {
    locked: isLockedAnywhere,
    reason: '',
    user: lockData?.user, // lock owner user
    origin: lockData?.origin,
  };

  if (!isLockedAnywhere) {
    return lockProps;
  }

  if (isCollabMode) {
    if (!isLockedByNXT) {
      lockProps.reason = t('blox.editorial.locked.reason.env');
    }
  } else {
    if (!isLockedByNXT) {
      lockProps.reason = t('blox.editorial.locked.reason.env');
    } else if (!isLockedByThisUser) {
      lockProps.reason = t('blox.editorial.locked.reason.user');
    } else {
      lockProps.reason = t('blox.editorial.locked.reason.default'); // default fallback
    }
  }

  return lockProps;
}

export const AssetLocked = memo(
  ({ lockData, isCollabMode, userUuid }: AssetLockedProps) => {
    const { locked, reason, user, origin } = buildLockoutInfo({
      lockData,
      userUuid,
      isCollabMode,
    });
    const theme = useTheme();
    const navigate = useNavigate();
    const { queryClient } = useCosmosQueryClient();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const { history } = useCosmosRuntime();
    const { activeSite, sessionConfig } = useCosmos();
    const { trackEvent } = useAnalytics();
    const { recordCopy, setReadOnlyState } = useAssetConfigContext();
    const originName = getLockOriginName(origin);
    const formatUser = formatAuthorData(user, { includeEmail: true });
    const showEmail = Boolean(user?.email?.toLowerCase() !== formatUser?.toLowerCase());
    const [dialogOpen, setDialogOpen] = useState(false);
    const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
    const { granted: userCanModifyAssets } = checkModifyAssetsPermission(sessionConfig);
    const canUnlock =
      (userIsAdmin || userCanModifyAssets) && origin === LEGACY_LOCK_ORIGIN;

    // Determine the back to search path and state
    const lastSearchWithParams = getBackToSearchLink('editorial', history); // eg "/editorial/search?query=Global+Leaders"  or empty string
    const hadLastSearch = Boolean(lastSearchWithParams); // if empty string, no search existed eg direct navd to asset
    const navBackPath = hadLastSearch
      ? `/${activeSite}${lastSearchWithParams}`
      : `/${activeSite}/editorial/search`;

    // Format the user display string
    const formattedUserDisplay = useMemo(() => {
      if (!formatUser) {
        return '';
      }
      return showEmail ? `${formatUser} (${user?.email})` : formatUser;
    }, [formatUser, showEmail, user?.email]);

    const buttonBackHandler = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault(); // Prevent hard nav
        navigate(navBackPath, { state: { reloadDefault: !hadLastSearch } });
      },
      [navigate, navBackPath, hadLastSearch],
    );

    /**
     * Continue in read-only mode.
     *
     * Set 'lockout_view' reason to state, indicating the user
     * chose to continue in read-only despite the lock.
     *
     * This is used as a flag for permissions checks effect logic in
     * AssetConfigContextProvider, to prevent overwriting the read-only
     * state if the user has chosen to continue regardless.
     */

    const buttonContinueHandler = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        setReadOnlyState({
          isReadOnly: true,
          message: t('blox.editorial.locked.help.readonly'),
          reason: 'lockout_view',
        });
      },
      [setReadOnlyState],
    );

    const handleUnlockClick = () => setDialogOpen(true);
    const handleDialogClose = () => setDialogOpen(false);

    // Handle unlock confirmation
    const handleConfirmUnlock = async () => {
      try {
        const assetId = recordCopy?.uuid;
        if (!assetId) {
          throw new Error('Missing asset ID for unlock.');
        }

        // Remove the existing lock
        await dataProvider.deleteAssetIdLock({
          path: { id: assetId },
          query: { force: true },
        });

        // Set the new lock
        await dataProvider.putAssetIdLock({
          path: { id: assetId },
          body: { origin: NXT_LOCK_ORIGIN },
        });

        // Log the force unlock event
        trackEvent({
          event: 'Asset Force Unlocked',
          data: {
            asset_id: assetId,
            user_id: userUuid,
            timestamp: new Date().toISOString(),
          },
        });

        // Close the dialog
        handleDialogClose();

        // Invalidate the lock query to trigger a refetch
        await queryClient.invalidateQueries({
          queryKey: ['getAssetIdLock', assetId],
        });

        // Notify unlock success
        notify(t('blox.editorial.locked.action.force_unlock.success'), {
          type: 'success',
        });

        // Reset any prior read-only state and let
        // the effect recalculate it as usual
        setReadOnlyState(initReadOnlyState);
      } catch (err) {
        console.error('Unlock failed:', err);

        notify(t('blox.editorial.locked.action.force_unlock.error'), {
          type: 'error',
        });

        // Close dialog even on error so user can try again
        handleDialogClose();
      }
    };

    // If not locked, return null and warn dev
    if (!locked) {
      console.warn('[AssetLocked] Rendered without a lock.');
      return null;
    }

    return (
      <Box
        id="AssetLocked"
        data-testid="AssetLocked"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          gap: 2,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <LockOutlinedIcon
            sx={{
              fontSize: bloxValues.iconSizeLarge,
              color: theme.palette.warning.light,
            }}
          />
          <Typography
            sx={{
              fontSize: bloxValues.megaFontSizeRem,
              color: theme.palette.text.primary,
            }}
          >
            {t('blox.editorial.locked.label')}
          </Typography>
        </Box>
        {reason && (
          <Typography
            sx={{
              fontSize: bloxValues.regularFontSizeRem,
              color: theme.palette.text.secondary,
            }}
          >
            {reason}
          </Typography>
        )}
        {originName && (
          <Typography
            sx={{
              fontSize: bloxValues.regularFontSizeRem,
              color: theme.palette.text.secondary,
            }}
          >
            {t('blox.editorial.locked.origin')} {originName}
          </Typography>
        )}
        {formatUser && (
          <Typography
            sx={{
              fontSize: bloxValues.regularFontSizeRem,
              color: theme.palette.text.secondary,
            }}
          >
            {t('blox.editorial.locked.by')} {formattedUserDisplay}
          </Typography>
        )}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            {canUnlock && (
              <>
                <Tooltip
                  title={t('blox.editorial.locked.action.force_unlock.label')}
                  placement="top"
                  disableInteractive
                >
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={
                      <LockOpenIcon sx={{ fontSize: bloxValues.iconSizeRegular }} />
                    }
                    onClick={handleUnlockClick}
                    data-testid="force-unlock-btn"
                  >
                    {t('blox.actions.unlock')}
                  </Button>
                </Tooltip>
                <ConfirmationDialog
                  open={dialogOpen}
                  onClose={handleDialogClose}
                  onConfirm={handleConfirmUnlock}
                  title={t('blox.editorial.locked.action.force_unlock.label')}
                  message={t('blox.editorial.locked.action.force_unlock.help', {
                    user: formattedUserDisplay,
                  })}
                  confirmButtonText={t('blox.actions.confirm')}
                  cancelButtonText={t('blox.actions.cancel')}
                  variant="warning"
                />
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              size="large"
              href={navBackPath}
              startIcon={<ArrowBackIcon sx={{ fontSize: bloxValues.iconSizeRegular }} />}
              onClick={buttonBackHandler}
              data-testid="asset-locked-back-btn"
              disableElevation
            >
              {t('blox.search.actions.return')}
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={buttonContinueHandler}
              endIcon={<ArrowForwardIcon sx={{ fontSize: bloxValues.iconSizeRegular }} />}
              data-testid="asset-locked-read-only-btn"
            >
              {t('blox.general.readonly')}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
);
